// The stock page's F&O tab: the nearest future (price, premium over the cash price, open interest,
// lot), implied volatility against the stock's own year, the move the options price in to expiry,
// the put/call ratio, today's futures build-up, and the option chain for the two nearest expiries,
// from Kite's quotes via the API (internal/options/live.go). Every figure carries what MoneyHQ's
// tests found: context to learn from, not a signal.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Pills, DeskIcon } from './market-ui';
import { openDesk } from './desk';

interface Leg { ltp: number; oi: number; volume: number; bid?: number; ask?: number; iv?: number; stale?: boolean }
interface Row { strike: number; call?: Leg; put?: Leg }
interface Expiry { expiry: string; days: number; forward: number; atmStrike: number; atmIV?: number; straddle?: number; straddlePct?: number; callOI: number; putOI: number; pcrOI?: number; rows: Row[] }
export interface FnoView {
  symbol: string; spot: number; at: string; lotSize?: number; iv30?: number; ivRank?: number; ivRankSessions?: number;
  future?: { expiry: string; ltp: number; oi: number; volume: number; premiumPct: number; lotSize?: number };
  expiries: Expiry[]; tested: Record<string, string>;
}

const px = (v: number) => v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const lakh = (v: number) => (v >= 1e7 ? `${(v / 1e7).toFixed(2)} Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(1)} L` : v.toLocaleString('en-IN'));
const day = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** The F&O view of a symbol, refreshed every minute while shown. */
export function useFno(symbol: string, live: boolean) {
  const [view, setView] = useState<FnoView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let stop = false;
    const load = () => api(`/v1/fno/${encodeURIComponent(symbol)}`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (stop) return;
      if (r.ok) { setView(j); setError(null); } else setError(j.error ?? 'The F&O view is unavailable right now.');
    }).catch(() => { if (!stop) setError('The F&O view is unavailable right now.'); });
    void load();
    const iv = live ? setInterval(load, 60_000) : undefined;
    return () => { stop = true; if (iv) clearInterval(iv); };
  }, [symbol, live]);
  return { view, error };
}

export function FnoPanel({ symbol, view, error, buildUp, oiChgPct }: { symbol: string; view: FnoView | null; error: string | null; buildUp: string; oiChgPct: number }) {
  const [exp, setExp] = useState(0);
  if (error) return <p className="py-8 text-center text-[13px] text-muted-foreground">{error}</p>;
  if (!view) return <p className="py-8 text-center text-[13px] text-muted-foreground">Loading futures and options…</p>;
  const e = view.expiries[Math.min(exp, view.expiries.length - 1)];
  const f = view.future;
  const maxOI = Math.max(1, ...(e?.rows ?? []).flatMap((r) => [r.call?.oi ?? 0, r.put?.oi ?? 0]));
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Tile label={f ? `Future · ${day(f.expiry)}` : 'Future'} value={f ? `₹${px(f.ltp)}` : '—'}
          note={f ? `${f.premiumPct >= 0 ? '+' : '−'}${Math.abs(f.premiumPct).toFixed(2)}% vs cash · OI ${lakh(f.oi)}${view.lotSize ? ` · lot ${view.lotSize}` : ''}` : 'no future'} />
        <Tile label="Implied volatility (30-day)" value={view.iv30 ? `${view.iv30.toFixed(1)}%` : '—'} title={view.tested.iv}
          note={view.ivRank != null ? `higher than ${Math.floor(view.ivRank)}% of the last ${view.ivRankSessions} sessions` : 'no stored history to rank it'} />
        <Tile label={e ? `Move priced in · to ${day(e.expiry)}` : 'Move priced in'} value={e?.straddlePct ? `±${e.straddlePct.toFixed(1)}%` : '—'} title={view.tested.straddle}
          note={e?.straddle ? `ATM ${e.atmStrike} straddle ₹${px(e.straddle)} · ${e.days} days` : 'no current prices at the money'} />
        <Tile label="Put/call OI ratio" value={e?.pcrOI ? e.pcrOI.toFixed(2) : '—'} title={view.tested.pcr}
          note={`${e?.rows.length ?? 0} strikes near the price · ${day(e?.expiry ?? view.at.slice(0, 10))}`} />
      </div>

      <p className="rounded-xl border bg-card px-3 py-2 text-[13px]">
        <span className="font-medium text-ink">Futures today: {buildUp}</span>
        <span className="text-muted-foreground"> (OI {oiChgPct >= 0 ? '+' : '−'}{Math.abs(oiChgPct).toFixed(1)}% vs yesterday). {view.tested.buildup}</span>
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Pills size="sm" label="Expiry" value={String(exp)} onChange={(v) => setExp(Number(v))}
          options={view.expiries.map((x, i) => ({ id: String(i), label: `${day(x.expiry)} · ${x.days}d` }))} />
        <button type="button" className="inline-flex items-center gap-1 text-[12.5px] text-brand-strong hover:underline"
          onClick={() => openDesk(`Explain ${symbol}'s option chain on screen: the implied volatility, the move priced in to expiry and the open interest, and what MoneyHQ's tests found about each.`)}>
          <DeskIcon className="size-3.5" />Explain this chain
        </button>
      </div>

      {e && (
        <Card className="gap-0 overflow-x-auto py-0">
          <table className="w-full text-[12.5px] sm:min-w-[720px]">
            <thead className="text-[11px] tracking-wide text-muted-foreground uppercase">
              <tr className="border-b"><th colSpan={4} className="py-1.5 text-center font-medium text-bull max-sm:hidden">Calls</th><th colSpan={3} className="py-1.5 text-center font-medium text-bull sm:hidden">Calls</th><th /><th colSpan={4} className="py-1.5 text-center font-medium text-bear max-sm:hidden">Puts</th><th colSpan={3} className="py-1.5 text-center font-medium text-bear sm:hidden">Puts</th></tr>
              <tr className="border-b text-right">
                <th className="px-2 py-1.5 font-medium">OI</th><th className="px-2 py-1.5 font-medium max-sm:hidden">Volume</th><th className="px-2 py-1.5 font-medium">IV</th><th className="px-2 py-1.5 font-medium">Price</th>
                <th className="px-2 py-1.5 text-center font-medium">Strike</th>
                <th className="px-2 py-1.5 font-medium">Price</th><th className="px-2 py-1.5 font-medium">IV</th><th className="px-2 py-1.5 font-medium max-sm:hidden">Volume</th><th className="px-2 py-1.5 font-medium">OI</th>
              </tr>
            </thead>
            <tbody>
              {e.rows.map((r) => {
                const atm = r.strike === e.atmStrike, itmCall = r.strike < e.forward, itmPut = r.strike > e.forward;
                return (
                  <tr key={r.strike} className={cn('border-b border-border/50 last:border-b-0', atm && 'bg-brand-soft/50 font-medium')}>
                    <LegCells leg={r.call} itm={itmCall} maxOI={maxOI} side="call" />
                    <td className="num px-2 py-1 text-center font-semibold text-ink">{r.strike}{atm && <div className="text-[10px] font-normal text-brand-strong">ATM</div>}</td>
                    <LegCells leg={r.put} itm={itmPut} maxOI={maxOI} side="put" />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <div className="grid gap-1 text-[12px] text-muted-foreground">
        <p><b className="font-medium text-ink">What MoneyHQ's tests found.</b> {view.tested.straddle} {view.tested.pcr} {view.tested.iv}</p>
        <p>{view.tested.selling}</p>
        <p>Prices from Kite, {view.at}; greyed prices didn't trade in the latest session and are left out of the IV and the move priced in. Shaded cells are in the money. IV is computed by MoneyHQ (Black-76). For learning, not investment advice; paper trading covers shares only.</p>
      </div>
    </div>
  );
}

function LegCells({ leg, itm, maxOI, side }: { leg?: Leg; itm: boolean; maxOI: number; side: 'call' | 'put' }) {
  const cls = cn('num px-2 py-1 text-right', itm && 'bg-muted/50', leg?.stale && 'text-muted-foreground/60');
  if (!leg) return <><td className={cls}>—</td><td className={cn(cls, 'max-sm:hidden')} /><td className={cls} /><td className={cls} /></>;
  const bar = <div className={cn('ml-auto h-1 rounded-full', side === 'call' ? 'bg-bull/50' : 'bg-bear/50')} style={{ width: `${Math.round((leg.oi / maxOI) * 100)}%` }} />;
  const cells = [
    <td key="oi" className={cls}>{lakh(leg.oi)}{bar}</td>,
    <td key="v" className={cn(cls, 'max-sm:hidden')}>{leg.volume ? lakh(leg.volume) : '—'}</td>,
    <td key="iv" className={cls}>{leg.iv ? `${leg.iv.toFixed(1)}%` : '—'}</td>,
    <td key="p" className={cls} title={leg.stale ? 'No trade in the latest session: an old price' : leg.bid || leg.ask ? `bid ${leg.bid ?? '—'} · ask ${leg.ask ?? '—'}` : undefined}>{px(leg.ltp)}</td>,
  ];
  return <>{side === 'call' ? cells : [...cells].reverse()}</>;
}

function Tile({ label, value, note, title }: { label: string; value: string; note?: string; title?: string }) {
  return (
    <div className="rounded-xl border bg-card px-3 py-2.5" title={title}>
      <div className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{label}</div>
      <div className="num mt-0.5 text-[18px] font-semibold text-ink">{value}</div>
      {note && <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground" title={note}>{note}</div>}
    </div>
  );
}
